import { BrowserRouter, Navigate, Route, Routes } from 'react-router-dom'
import { QueryClient, QueryClientProvider } from '@tanstack/react-query'
import { SiteHeader } from './components/SiteHeader'
import { ScrollToTop } from './components/ScrollToTop'
import { ScrollJump } from './components/ScrollJump'
import { Dashboard } from './pages/Dashboard'
import { FleetRegister } from './pages/FleetRegister'
import { VehicleDetail } from './pages/VehicleDetail'
import { RtoRecord } from './pages/RtoRecord'
import { Notifications } from './pages/Notifications'
import { ExportScreen } from './pages/ExportScreen'
import { ImportScreen } from './pages/ImportScreen'
import { AddVehicle } from './pages/AddVehicle'
import { ReportScreen } from './pages/ReportScreen'
import { IntakeQueue } from './pages/IntakeQueue'
import { Reminders } from './pages/Reminders'
import { MachineryRegister } from './pages/MachineryRegister'
import { MachineryDetail } from './pages/MachineryDetail'
import { MachineryImport } from './pages/MachineryImport'
import { MachineryNew } from './pages/MachineryNew'
import { MachineryExport } from './pages/MachineryExport'

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      // Compliance dates change rarely — refetching on every window focus would
      // be noise, and on a phone it means a request every time the user switches
      // apps and comes back.
      refetchOnWindowFocus: false,
      staleTime: 30_000,
      // One retry: a genuine outage should surface quickly as "you are offline"
      // rather than being masked by half a minute of silent retrying.
      retry: 1,
    },
  },
})

export default function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <BrowserRouter>
        {/* Both sit outside <Routes>: they are the same on every screen and must
            not unmount and remount as the route changes. */}
        <ScrollToTop />
        <SiteHeader />

        <Routes>
          {/* The dashboard is the landing page: "what should we do this month"
              is the question somebody arriving has, and the register answers
              "what is the state of vehicle X". Dashboard.tsx redirects to
              /register when it sees a register filter in the query string, so
              the filtered links already shared by email keep working. */}
          <Route path="/" element={<Dashboard />} />
          <Route path="/register" element={<FleetRegister />} />

          {/*
            Static before dynamic. React Router ranks static segments higher
            regardless of order, but writing them in this order makes it obvious
            that /vehicles/new is a screen and not a vehicle called "new".
          */}
          <Route path="/vehicles/new" element={<AddVehicle />} />
          <Route path="/vehicles/:registration" element={<VehicleDetail />} />
          {/*
            Nested under the vehicle it describes, so Back returns there and the URL
            says what it is. A page rather than a modal: it is a document you read,
            and routing here is full pages.
          */}
          <Route path="/vehicles/:registration/rto" element={<RtoRecord />} />
          <Route path="/notifications" element={<Notifications />} />
          <Route path="/export" element={<ExportScreen />} />

        {/* Machinery. A third place you live in, not a task you finish — which is
            why it is a tab and /import is not. `new` and `import` come BEFORE
            `:machineryId`, or react-router matches them as ids. */}
        <Route path="/machinery" element={<MachineryRegister />} />
        <Route path="/machinery/new" element={<MachineryNew />} />
        <Route path="/machinery/import" element={<MachineryImport />} />
        <Route path="/machinery/export" element={<MachineryExport />} />
        <Route path="/machinery/:machineryId" element={<MachineryDetail />} />
          <Route path="/import" element={<ImportScreen />} />

          {/* The chosen ids live in the query string: a report's value is that it
              can be re-run, bookmarked and sent to somebody. */}
          <Route path="/report" element={<ReportScreen />} />

          {/* Certificates that arrived by email, waiting for somebody to agree
              with what was read off them. */}
          <Route path="/intake" element={<IntakeQueue />} />

          {/* Standing instructions to warn people before certificates lapse.
              Not a tab: it is set up once and then left alone, and putting it
              beside the two screens you live in would make both harder to
              find. Reached from the bell, which is where it was always
              promised. */}
          <Route path="/reminders" element={<Reminders />} />

          {/* An unknown path is far likelier to be a stale bookmark than a
              meaningful destination, so send it to the register rather than
              inventing a 404 screen. */}
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>

        {/* After the routes so it paints above them, and outside so it measures
            the page rather than remounting on every navigation. */}
        <ScrollJump />
      </BrowserRouter>
    </QueryClientProvider>
  )
}
